import { Component } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'reactstrap';
import { DataGrid, DataGridColumnDef, DataFieldType, ModalButtonType } from '@Components';
import { ImportedSalaryTableStore } from './ImportedSalaryTableStore';
import { ImportTypeModel, PermissionType, SalaryItemViewModel, SalaryPeriodResponseItem, SalaryPeriodStatus } from '@Models';
import { DateTimeService, securityService } from '@Services';
import { modalService } from '@Components/Modal/ModalService';
import { CreateSalaryPeriodItemDialog } from '../CreateSalaryPeriodItemDialog/CreateSalaryPeriodItemDialog';
import { InputCell } from '@Pages/RuleEditor/Components/InputCell/InputCell';
import { MoneyCell } from '@Pages/RuleEditor/Components/MoneyCell/MoneyCell';
import { FaTimes } from 'react-icons/fa';
import { ERROR_TO_LONG, ImportSalaryType, IMPORT_FINISHED } from '@AppConstants';
import './imported-salary-table.scss';

type ImportedSalaryTableProps = {
    personId: string;
    period: SalaryPeriodResponseItem;
    importType: ImportTypeModel;
    onResizeHeader?: (width?: number, height?: number) => void;
    onChanged?: (personId: string) => void;
    headerHeight?: number;
};

@observer
class ImportedSalaryTable extends Component<ImportedSalaryTableProps> {
    private _store: ImportedSalaryTableStore;

    constructor(props: ImportedSalaryTableProps) {
        super(props);
        this._store = new ImportedSalaryTableStore();
    }

    componentDidMount() {
        document.addEventListener(IMPORT_FINISHED, this._handleImportFinish);
        void this._store.loadData(this.props.personId, this.props.period.id, this.props.importType.id);
    }

    componentDidUpdate(prevProps: ImportedSalaryTableProps) {
        if (prevProps.personId !== this.props.personId || prevProps.period.id !== this.props.period.id || prevProps.importType.id !== this.props.importType.id) {
            this._store.cancel();
            void this._store.loadData(this.props.personId, this.props.period.id, this.props.importType.id);
        }
    }

    componentWillUnmount() {
        document.removeEventListener(IMPORT_FINISHED, this._handleImportFinish);
        this._store.cancel();
    }

    private _handleImportFinish = () => {
        void this._store.loadData(this.props.personId, this.props.period.id, this.props.importType.id);
    };

    render() {
        const { onResizeHeader, importType, headerHeight } = this.props;
        const { dataSource } = this._store;
        const permission = this._getPermission(importType);
        const showCreateButton = !!permission && securityService.hasPermission(permission);

        return (
            <div className={`imported-salary-table${showCreateButton ? ' has-create-btn' : ''}`}>
                {showCreateButton &&
                    <Button
                        color="success"
                        size="sm"
                        className="imported-salary-table-create-btn"
                        onClick={this._createClickHandler}
                        disabled={this.props.period.status === SalaryPeriodStatus.Released || (this.props.period.status === SalaryPeriodStatus.ReadyToRelease && !securityService.hasPermission(PermissionType.ReleaseImportPeriod))}
                    >
                        Create
                    </Button>
                }
                <div className="imported-salary-table-grid-wrapper">
                    <DataGrid<SalaryItemViewModel>
                        tableKey={`imported-salary-${importType.id}`}
                        columns={this._columns}
                        items={dataSource}
                        initialItemsList={dataSource}
                        filteredItemsWithoutColumnFilters={dataSource}
                        loading={this._store.loader.isPending}
                        maxHeight="100%"
                        disableSelection
                        headerHeight={headerHeight}
                        onHeaderHeightChange={(h) => onResizeHeader?.(undefined, h)}
                    />
                </div>
            </div>
        );
    }

    private get _columns(): DataGridColumnDef<SalaryItemViewModel>[] {
        return [
            {
                title: 'Date',
                dataField: 'date',
                dataFieldType: DataFieldType.Date,
                disableSorting: true,
                disableFiltering: true,
                renderCell: (_value, item) => <>{item.date ? DateTimeService.toUiDate(item.date) : ''}</>,
                minWidth: '100px',
                maxWidth: '100px'
            },
            {
                title: 'Amount',
                dataField: 'amount',
                dataFieldType: DataFieldType.Number,
                disableSorting: true,
                disableFiltering: true,
                renderCell: (_value, item) => this._renderAmountCell(item),
                maxWidth: '150px'
            },
            {
                title: 'Unit',
                dataField: 'unit',
                disableSorting: true,
                disableFiltering: true,
                maxWidth: '120px'
            },
            {
                title: 'Comments',
                dataField: 'comments',
                disableSorting: true,
                disableFiltering: true,
                renderCell: (_value, item) => this._renderCommentsCell(item)
            },
            {
                title: 'Actions',
                dataField: 'id',
                disableSorting: true,
                disableFiltering: true,
                renderCell: (_value, item) => this._renderActionCell(item),
                maxWidth: '70px'
            }
        ];
    }

    private _renderAmountCell(item: SalaryItemViewModel) {
        const { importType, personId, onChanged } = this.props;
        const permission = this._getPermission(importType);
        const isDisabled = !permission || !securityService.hasPermission(permission)
            || this.props.period.status === SalaryPeriodStatus.Released
            || (this.props.period.status === SalaryPeriodStatus.ReadyToRelease && !securityService.hasPermission(PermissionType.ReleaseImportPeriod));

        const changeHandler = async (value: number) => {
            const modifiedItem = Object.assign({}, item);
            modifiedItem.amount = value;
            await this._store.updateItem(modifiedItem);
            if (onChanged) onChanged(personId);
        };

        return (
            <MoneyCell
                initialValue={item.amount}
                onChange={changeHandler}
                disabled={isDisabled}
            />
        );
    }

    private _renderCommentsCell(item: SalaryItemViewModel) {
        const { importType, personId, onChanged } = this.props;
        const permission = this._getPermission(importType);
        const isDisabled = !permission || !securityService.hasPermission(permission)
            || this.props.period.status === SalaryPeriodStatus.Released
            || (this.props.period.status === SalaryPeriodStatus.ReadyToRelease && !securityService.hasPermission(PermissionType.ReleaseImportPeriod));

        const changeHandler = async (value: string) => {
            const modifiedItem = Object.assign({}, item);
            modifiedItem.comments = value;
            await this._store.updateItem(modifiedItem);
            if (onChanged) onChanged(personId);
        };

        return (
            <InputCell
                validate={value => value.length > 255 ? ERROR_TO_LONG : undefined}
                initialValue={item.comments ? '' + item.comments : ''}
                onChange={changeHandler}
                disabled={isDisabled}
            />
        );
    }

    private _renderActionCell(item: SalaryItemViewModel) {
        const { importType, personId, onChanged } = this.props;
        const permission = this._getPermission(importType);
        const isDisabled = !permission || !securityService.hasPermission(permission)
            || this.props.period.status === SalaryPeriodStatus.Released
            || (this.props.period.status === SalaryPeriodStatus.ReadyToRelease && !securityService.hasPermission(PermissionType.ReleaseImportPeriod));

        return (
            <FaTimes
                className={'delete-button' + (isDisabled ? '-disabled' : '')}
                onClick={async () => {
                    if (isDisabled) return;
                    if (!await modalService.showConfirmation('Do you really want to remove item?', { color: 'danger' })) return;
                    await this._store.deleteItem(item);
                    if (onChanged) onChanged(personId);
                }}
                title="Delete"
            />
        );
    }

    private _createClickHandler = async () => {
        const { period, personId, importType, onChanged } = this.props;
        const { button } = await modalService.show(CreateSalaryPeriodItemDialog, { period, personId, importTypeId: importType.id, title: importType.name });

        if (button === ModalButtonType.Save) {
            await this._store.loadData(personId, period.id, importType.id);
            if (onChanged) onChanged(personId);
        }
    };

    private _getPermission = (importType: ImportTypeModel) => {
        switch (importType.id) {
            case ImportSalaryType.tickets: return PermissionType.EditTickets;
            case ImportSalaryType.ticketsRefund: return PermissionType.EditTicketRefunds;
            case ImportSalaryType.dutyFree: return PermissionType.EditDutyFree;
            case ImportSalaryType.shiftBonus: return PermissionType.EditShiftBonus;
            default: return null;
        }
    };
}

export { ImportedSalaryTable };
